'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2CheckSummary } from '@/lib/pr-v2/check-run-presentation';
import { useQuery } from '@tanstack/react-query';
import { fetchInboxV2 } from '@/data/pull-requests/fetch-inbox-v2';
import { INBOX_V2_SIDE_LOAD_QUERY_OPTIONS } from '@/data/pull-requests/inbox-v2-side-load';
import { inboxV2CheckSummariesQueryKey } from '@/data/pull-requests/query-keys';
import { useSelectedRepositories } from '@/lib/hooks/use-selected-repositories';
type InboxV2CheckSummariesOptions = {
  enabled?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean;
};
type InboxV2CheckSummariesSection = 'open' | 'merged';
type InboxV2CheckSummariesQueryKey = readonly [string, string, string, InboxV2CheckSummariesSection, number | null | undefined, string | null];
async function getInboxV2CheckSummariesQueryFn({
  queryKey,
  signal
}: {
  queryKey: InboxV2CheckSummariesQueryKey;
  signal: AbortSignal;
}): Promise<Record<string, PrV2CheckSummary>> {
  const [,,, section, installationId] = queryKey;
  const data = await fetchInboxV2<{
    checks?: Record<string, PrV2CheckSummary>;
  }>({
    path: '/inbox/api/pull-requests/checks',
    installationId,
    query: {
      section
    },
    signal,
    description: `${section} check summaries`
  });
  return data.checks ?? {};
}
export function useInboxV2CheckSummaries(section, options) {
  const $ = _c(14);
  const queryEnabled = options?.enabled ?? true;
  let t0;
  if ($[0] !== queryEnabled) {
    t0 = {
      enabled: queryEnabled
    };
    $[0] = queryEnabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    selectedRepositoryIds: t1,
    normalizedInstallationId
  } = useSelectedRepositories(t0);
  let t2;
  if ($[2] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedRepositoryIds = t2;
  let t3;
  if ($[4] !== normalizedInstallationId || $[5] !== section || $[6] !== selectedRepositoryIds) {
    t3 = inboxV2CheckSummariesQueryKey(section, normalizedInstallationId, selectedRepositoryIds);
    $[4] = normalizedInstallationId;
    $[5] = section;
    $[6] = selectedRepositoryIds;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = queryEnabled && selectedRepositoryIds.length > 0;
  const t5 = options?.staleTime ?? INBOX_V2_SIDE_LOAD_QUERY_OPTIONS.staleTime;
  const t6 = options?.refetchOnWindowFocus ?? INBOX_V2_SIDE_LOAD_QUERY_OPTIONS.refetchOnWindowFocus;
  const t7 = options?.refetchOnMount ?? false;
  let t8;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      queryKey: t3,
      queryFn: _temp,
      ...INBOX_V2_SIDE_LOAD_QUERY_OPTIONS,
      enabled: t4,
      staleTime: t5,
      refetchOnWindowFocus: t6,
      refetchOnMount: t7
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return useQuery(t8);
}
function _temp(args) {
  return getInboxV2CheckSummariesQueryFn({
    queryKey: args.queryKey as InboxV2CheckSummariesQueryKey,
    signal: args.signal
  });
}